import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useState, useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { ArrowLeft, Target, Plus, ChevronDown, FileText, Globe } from 'lucide-preact';
import { campaignApi } from '../../api/campaign';
import { CampaignListModal } from './campaignList/CampaignListModal';
import { CampaignTabbedContent } from './CampaignTabbedContent';
import { NewWebModal } from '@/components/webs/NewWebModal';
import { useUserProfile } from '@/hooks/useUserProfile';
import { BRAND_CLASSES } from '../../constants/theme';
import { usePageHeader } from '@/hooks/usePageHeader';
import { routes } from '@/utils/routes';
import { useAIChatContextUpdater } from '@/hooks';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
export function CampaignDetail({ clientCompanyId, selectedWorkspaceId, campaignId }) {
    const location = useLocation();
    const { setHeader } = usePageHeader();
    const { getWorkspaceCapabilities } = useUserProfile();
    const [campaign, setCampaign] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCampaignListModalOpen, setIsCampaignListModalOpen] = useState(false);
    const [isNewWebModalOpen, setIsNewWebModalOpen] = useState(false);
    const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
    const { updateContext } = useAIChatContextUpdater();
    // Reset AI context to campaign-level View mode when modals close
    const resetAIContext = () => {
        updateContext({
            workspaceId: selectedWorkspaceId,
            campaignId,
            location: ChatLocations.CampaignDetails,
            entityType: ChatEntityTypes.Campaign,
            entityId: campaignId,
            viewMode: ChatViewModes.View,
            availableFields: [],
            availableActions: [],
        });
    };
    // Get workspace capabilities from server-computed flags
    const { canEdit } = getWorkspaceCapabilities(clientCompanyId, selectedWorkspaceId);
    // Can create if user can edit (canEdit includes admin and editor roles)
    const canCreate = canEdit;
    useEffect(() => {
        loadCampaign();
    }, [campaignId]);
    // Set static breadcrumbs (avoid data-dependent names to reduce flicker)
    useEffect(() => {
        const r = routes.scoped(clientCompanyId, selectedWorkspaceId);
        setHeader({
            items: [
                { text: 'Campaigns', href: r.campaignsIndex() },
                { text: 'Campaign' },
            ]
        });
        return () => { };
    }, [clientCompanyId, selectedWorkspaceId, campaignId]);
    const loadCampaign = async () => {
        try {
            setIsLoading(true);
            setError(null);
            // For now, we'll simulate finding the campaign from the campaigns list
            // TODO: Replace with actual getCampaignById API call when available
            const response = await campaignApi.getCampaignsByWorkspace(selectedWorkspaceId);
            const foundCampaign = response.data?.find(c => c.Id === campaignId);
            if (foundCampaign) {
                setCampaign(foundCampaign);
            }
            else {
                setError('Campaign not found');
            }
        }
        catch (err) {
            setError('Failed to load campaign details');
            console.error('Error loading campaign:', err);
        }
        finally {
            setIsLoading(false);
        }
    };
    const handleBackClick = () => {
        location.route(routes.scoped(clientCompanyId, selectedWorkspaceId).campaignsIndex());
    };
    const handleCampaignUpdated = (updatedCampaign) => {
        setCampaign(updatedCampaign);
    };
    const handleNewCampaignListClick = () => {
        setIsCreateMenuOpen(false);
        setIsCampaignListModalOpen(true);
    };
    const handleNewWebClick = () => {
        setIsCreateMenuOpen(false);
        setIsNewWebModalOpen(true);
    };
    const [latestAudienceFile, setLatestAudienceFile] = useState(null);
    const handleCampaignListSaved = (newAudienceFile) => {
        setIsCampaignListModalOpen(false);
        resetAIContext();
        // Optimistically set latest audience for AudienceTab append
        setLatestAudienceFile(newAudienceFile);
    };
    // Close create menu on outside click
    useEffect(() => {
        if (!isCreateMenuOpen)
            return;
        const onClick = (e) => {
            const target = e.target;
            if (!target.closest?.('#create-dropdown')) {
                setIsCreateMenuOpen(false);
            }
        };
        document.addEventListener('click', onClick);
        return () => document.removeEventListener('click', onClick);
    }, [isCreateMenuOpen]);
    if (isLoading) {
        return (_jsxs("div", { className: "p-6", children: [_jsx("div", { className: "flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4", children: _jsxs("div", { className: "min-w-0", children: [_jsx("h1", { className: "text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white", children: "Campaign" }), _jsx("p", { className: "text-slate-600 dark:text-slate-300", children: "Campaign Details & Analytics" })] }) }), _jsxs("div", { className: "space-y-4", children: [_jsx("div", { className: "h-32 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse" }), _jsx("div", { className: "h-64 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse" })] })] }));
    }
    if (error || !campaign) {
        return (_jsx("div", { className: "p-6", children: _jsx("div", { className: "flex items-center justify-center min-h-[24rem]", children: _jsxs("div", { className: "text-center max-w-md mx-auto", children: [_jsxs("div", { className: "relative w-20 h-20 mx-auto mb-6", children: [_jsx("div", { className: "absolute inset-0 rounded-2xl bg-gradient-to-tr from-primary-500/20 to-primary-400/10 blur-sm" }), _jsx("div", { className: "relative w-full h-full rounded-2xl bg-primary-50 dark:bg-primary-900/10 flex items-center justify-center border border-primary-200/60 dark:border-primary-800/40 shadow-inner", children: _jsx(Target, { className: "w-9 h-9 text-primary-600 dark:text-primary-300" }) })] }), _jsx("h3", { className: "text-xl font-semibold tracking-tight text-slate-900 dark:text-white mb-3", children: error || 'Campaign Not Found' }), _jsx("p", { className: "text-sm md:text-base text-slate-600 dark:text-slate-300 mb-6 leading-relaxed", children: "The campaign you're looking for could not be found or may have been removed." }), _jsx("div", { children: _jsxs("button", { onClick: handleBackClick, className: `${BRAND_CLASSES.BUTTON.PRIMARY} inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-slate-900`, children: [_jsx(ArrowLeft, { className: "w-4 h-4" }), "Back to Campaigns"] }) })] }) }) }));
    }
    return (_jsxs("div", { className: "p-6", children: [_jsxs("div", { className: "flex flex-col md:flex-row md:items-start justify-between mb-8 gap-4", children: [_jsxs("div", { className: "min-w-0 flex-1", children: [_jsx("h1", { className: "text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white break-words line-clamp-2 hover:line-clamp-none transition-all duration-200 cursor-pointer max-w-[58ch] md:max-w-[54ch]", title: campaign.Name, onClick: e => {
                                    const el = e.currentTarget;
                                    if (el.classList.contains('line-clamp-2')) {
                                        el.classList.remove('line-clamp-2');
                                    }
                                    else {
                                        el.classList.add('line-clamp-2');
                                    }
                                }, children: campaign.Name }), _jsx("p", { className: "text-slate-600 dark:text-slate-300 mt-1", children: "Campaign Details & Analytics" })] }), _jsx("div", { className: "flex items-center gap-3 flex-shrink-0", children: canCreate && (_jsxs("div", { className: "relative", id: "create-dropdown", children: [_jsxs("button", { onClick: () => setIsCreateMenuOpen(v => !v), className: `inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${isCreateMenuOpen
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-primary-600 text-white hover:bg-primary-700'}`, children: [_jsx(Plus, { className: "w-4 h-4" }), "New", _jsx(ChevronDown, { className: "w-4 h-4" })] }), isCreateMenuOpen && (_jsxs("div", { className: "absolute right-0 mt-2 w-56 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-10 overflow-hidden", children: [_jsxs("button", { onClick: handleNewCampaignListClick, className: "w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700", children: [_jsx(FileText, { className: "w-4 h-4" }), "New Audience"] }), _jsxs("button", { onClick: handleNewWebClick, className: "w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700", children: [_jsx(Globe, { className: "w-4 h-4" }), "New Web"] })] }))] })) })] }), _jsx(CampaignTabbedContent, { campaign: campaign, audienceAppend: latestAudienceFile, clientCompanyId: clientCompanyId, selectedWorkspaceId: selectedWorkspaceId, onCampaignUpdated: handleCampaignUpdated }), _jsx(CampaignListModal, { isOpen: isCampaignListModalOpen, onClose: () => { setIsCampaignListModalOpen(false); resetAIContext(); }, campaignId: campaignId, clientCompanyId: clientCompanyId, selectedWorkspaceId: selectedWorkspaceId, onCampaignListSaved: handleCampaignListSaved }), isNewWebModalOpen && (_jsx(NewWebModal, { isOpen: isNewWebModalOpen, onClose: () => { setIsNewWebModalOpen(false); resetAIContext(); }, campaignId: campaignId, clientCompanyId: clientCompanyId, workspaceId: selectedWorkspaceId, onRequestNewAudience: () => {
                    setIsNewWebModalOpen(false);
                    setIsCampaignListModalOpen(true);
                }, onWebCreated: (w) => {
                    setIsNewWebModalOpen(false);
                    resetAIContext();
                    console.log('New web created:', w);
                } }))] }));
}
